import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { replace } from 'react-router-redux';
import {
  Button,
  Dimmer,
  Header,
  Icon,
  Loader,
  Modal,
} from 'semantic-ui-react';

import { loadCRMLinkSession } from '../../actions/crmLink';
import {
  destinationFor,
  forgetLinkID,
  linkIDFromRoute,
  newRequestGuard,
  rememberLinkID,
  rejectionFor,
} from '../../utils/crmLink';

// Where a salesperson lands after opening a CRM deep link (MS-6620, T4.6).
//
// book-api verified the signed link on its own server and redirected here with an opaque session
// id and nothing else. This page reads the session — which is also what binds it to whoever opened
// it — and moves on to the booking form, the project's unit list, or the project list, depending
// on how much the link named. Nothing about the buyer is ever put in a URL.
//
// The page is deliberately a spinner and, when it cannot go on, one plain sentence with a
// reference id. A salesperson is standing in front of a customer on a gallery tablet; the useful
// thing is what to do next, not what went wrong inside.
export const REJECTION_COPY = {
  not_found: 'That OutPerform link is not one we recognise. Open the booking again from OutPerform.',
  expired: 'That link has expired. Open the booking again from OutPerform to get a fresh one.',
  consumed: 'That link has already been used for a booking. Open the booking again from OutPerform.',
  other_user: 'That link was opened by a colleague. Ask them to finish the booking, or open it again from OutPerform on this device.',
  provider_disabled: 'CRM links are switched off for this company. Contact MHub support.',
  unknown_provider: 'That link is for a CRM this company does not use. Contact MHub support.',
  too_many_parameters: 'That link could not be read. Open the booking again from OutPerform.',
  unavailable: 'We could not read that link just now. Try again in a moment.',
};

// The id comes only from the URL book-api sent us to. It is mirrored into the tab so the booking
// form can find it (MS-6621), but this page never reads the mirror back: falling back to it would
// let a bare /crm/continue/ silently reopen whatever session the tab happened to be holding.
const linkIDOf = props => linkIDFromRoute(props.params, props.location);

export class CrmContinue extends Component {
  state = { rejection: null };

  componentWillMount() {
    const preloader = document.getElementById('preloader-container');
    if (preloader) preloader.classList.remove('app-loading');
  }

  componentDidMount() {
    this.pickUpSession();
  }

  // React Router reuses this component when the id changes from one continue URL to another, so a
  // second link arriving in the same tab must start its own read rather than sit on the first's
  // result.
  componentWillReceiveProps(next) {
    if (linkIDOf(next) !== linkIDOf(this.props)) {
      this.pickUpSession(next);
    }
  }

  componentWillUnmount() {
    this.guard.invalidate(); // nothing still in flight may act after this
  }

  // Only the newest read may act; see newRequestGuard, where the rule and its tests live.
  guard = newRequestGuard();

  pickUpSession = (fromProps) => {
    // Claim the guard FIRST, before anything can return early. Navigating from a link that is
    // still loading to a URL with no id would otherwise leave the old read current: its answer
    // would arrive later and redirect into a session the salesperson had already left.
    const token = this.guard.begin();
    const id = linkIDOf(fromProps || this.props);
    if (!id) {
      this.setState({ rejection: rejectionFor('not_found', '') });
      return;
    }
    rememberLinkID(id);
    this.setState({ rejection: null });
    this.props.dispatch(loadCRMLinkSession(id)).then(({ session, rejection }) => {
      // Unmounted, or overtaken by a newer link: this answer is about a session nobody is opening
      // any more, so it must not redirect, must not clear the mirror, and must not set state.
      if (!this.guard.isCurrent(token)) return;
      if (rejection) {
        // A session that will never work again is not worth keeping in the tab: leaving it there
        // would make the booking form pick the dead one up.
        if (!rejection.retryable) forgetLinkID();
        this.setState({ rejection });
        return;
      }
      // replace, not push: the continue URL must not sit in the history for a back button to
      // land on after the session has been consumed.
      this.props.dispatch(replace(destinationFor(session, id)));
    });
  };

  render() {
    const { rejection } = this.state;
    if (!rejection) {
      return (
        <Dimmer active inverted>
          <Loader inline="centered">Opening the booking…</Loader>
        </Dimmer>
      );
    }
    return (
      <Modal open basic size="small">
        <Header icon="linkify" content="This link cannot be opened" />
        <Modal.Content>
          <h3>{REJECTION_COPY[rejection.reason] || REJECTION_COPY.unavailable}</h3>
          {rejection.reference ? (
            <p>
              Reference <code>{rejection.reference}</code> — quote this to MHub support.
            </p>
          ) : null}
        </Modal.Content>
        <Modal.Actions>
          {rejection.retryable ? (
            <Button color="green" inverted onClick={() => this.pickUpSession()}>
              <Icon name="redo" /> Try again
            </Button>
          ) : null}
          <Button
            color="grey"
            inverted
            onClick={(e) => { e.preventDefault(); this.props.dispatch(replace('/bookings/')); }}
          >
            <Icon name="list" /> Go to bookings
          </Button>
        </Modal.Actions>
      </Modal>
    );
  }
}

CrmContinue.propTypes = {
  dispatch: PropTypes.func.isRequired,
  // Read through linkIDOf rather than directly, which is why eslint cannot see the use.
  /* eslint-disable react/no-unused-prop-types */
  // The session id arrives as a path segment; the query form is accepted for links already issued.
  params: PropTypes.shape({ id: PropTypes.string }),
  // crm_link can arrive as an array when the parameter is repeated; linkIDFromRoute handles that
  // by ignoring it, and the type says so rather than warning about an input we expect.
  location: PropTypes.shape({
    query: PropTypes.shape({
      crm_link: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
    }),
  }),
  /* eslint-enable react/no-unused-prop-types */
};

CrmContinue.defaultProps = {
  params: {},
  location: {},
};

export default connect()(CrmContinue);



// WEBPACK FOOTER //
// ./src/containers/Crm/Continue.jsx